import { memo, useMemo } from 'react';
import { ChevronDownIcon } from '@page/icons';

import { Button, ButtonSize } from '../Button';
import { Menu, MenuClose, MenuContent, MenuTrigger } from '../Menu';
import { PopoverContentProps } from '../Popover';
import { Label } from '../Label';
import { cn } from '../../lib/styles';

import { CheckboxMenuItems } from './CheckboxMenuItems';
import { RadioMenuItems } from './RadioMenuItems';
import { MenuItem } from '../MenuItem';

export interface SelectOption<T> {
  value: T;
  label: React.ReactNode;
  description?: React.ReactNode;
  additionalContent?: React.ReactNode;
  disabled?: boolean;
}

export type SelectVariant = 'default' | 'simple';

export interface SelectProps<T> {
  id?: string;
  options: SelectOption<T | null | undefined>[];
  value?: T | null;
  values?: T[]; // For multiple selection
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  buttonClassName?: string;
  className?: string;
  menuClassName?: string;
  multiple?: boolean;
  buttonSuffix?: React.ReactNode;
  trigger?: React.ReactNode;
  align?: PopoverContentProps['align'];
  withinPortal?: boolean;
  size?: ButtonSize;
  variant?: SelectVariant;
  onValueChange?: (value: T | null | undefined) => void;
  onValuesChange?: (values: T[]) => void; // For multiple selection
  renderMultiSelectValue?: (values: T[]) => React.ReactNode;
}

function SelectBase<T>({
  id,
  size,
  variant = 'default',
  options,
  value,
  values,
  label,
  placeholder = 'Select option',
  error,
  disabled = false,
  className,
  buttonClassName,
  menuClassName,
  multiple = false,
  buttonSuffix,
  trigger,
  align = 'start',
  withinPortal = false,
  onValueChange,
  onValuesChange,
  renderMultiSelectValue,
}: SelectProps<T>) {
  const selectedOption = useMemo(() => {
    if (multiple) return null;

    return options.find((option) => option.value === value);
  }, [options, value, multiple]);

  if (options.length === 0) return null;

  const getButtonText = () => {
    if (!multiple) return selectedOption?.value ? selectedOption?.label : placeholder;

    if (!values || values.length === 0) return placeholder;

    if (values.length === 1) {
      const option = options.find((opt) => opt.value === values[0]);

      return option?.label || placeholder;
    }

    return renderMultiSelectValue?.(values) || `${values.length} selected`;
  };

  const renderMenuContent = () => {
    if (multiple) return <CheckboxMenuItems options={options} values={values} onValuesChange={onValuesChange} />;

    if (variant === 'simple')
      return (
        <>
          {options.map((option) => (
            <MenuClose key={String(option.value)}>
              <MenuItem active={selectedOption?.value === option.value} onClick={() => onValueChange?.(option.value)}>
                {option.label}
              </MenuItem>
            </MenuClose>
          ))}
        </>
      );

    return (
      <RadioMenuItems options={options} selectedOption={selectedOption} onValueChange={onValueChange}>
        {(children) => <MenuClose>{children}</MenuClose>}
      </RadioMenuItems>
    );
  };

  const menu = (
    <Menu>
      <MenuTrigger asChild>
        {trigger ?? (
          <Button
            type="button"
            variant="secondary"
            rightIcon={<ChevronDownIcon className="size-5 min-w-5" />}
            disabled={disabled}
            size={size}
            className={cn(
              'max-w-xl truncate [&_[data-slot="content"]]:justify-between fill-icon-primary px-2 font-medium',
              { 'border-text-destructive hover:border-text-destructive': !!error },
              label ? buttonClassName : className || buttonClassName,
              {
                'text-text-tertiary': multiple ? !values || values.length === 0 : !selectedOption,
              },
            )}
          >
            <div className="truncate">{getButtonText()}</div>

            {buttonSuffix}
          </Button>
        )}
      </MenuTrigger>

      <MenuContent
        className={cn('max-w-xl max-h-[446px] overflow-y-auto overflow-x-hidden', menuClassName)}
        align={align}
        withinPortal={withinPortal}
      >
        {renderMenuContent()}
      </MenuContent>
    </Menu>
  );

  if (!label) return menu;

  return (
    <div className={cn('flex flex-col', className)}>
      <Label htmlFor={id} className="mb-2">
        {label}
      </Label>

      {menu}

      <div className="h-5 pt-1 text-text-destructive font-body-sm">{error}</div>
    </div>
  );
}

export const Select = memo(SelectBase) as <T>(props: SelectProps<T>) => JSX.Element;
